import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import CustomTablePagination from '../../components/CustomTablePagination/CustomTablePagination';
import DeviationDetailsDialog from '../../dialogs/DeviationDetailsDialog/DeviationDetailsDialog';
import Deviation from '../../types/Deviation';
import api from '../../utils/api';
import { ActionTableCell, FlexContainer, StyledButton, Title } from './styled';

const DeviationsPage = () => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [deviations, setDeviations] = useState<Deviation[]>([]);
    const [selectedDeviationId, selectDeviationId] = useState(0);

    const [paginationPage, setPaginationPage] = useState(0);
    const [paginationTotal, setPaginationTotal] = useState(0);
    const [paginationItemsPerPage, setPaginationItemsPerPage] = useState(25);

    const [isDeviationDialogOpen, setIsDeviationDialogOpen] = useState(false);

    const getData = useCallback(() => {
        api.request('/deviations', 'GET', { page: paginationPage + 1, perPage: paginationItemsPerPage }).then((res) => {
            const { data, total } = res;
            setDeviations(data);
            setPaginationTotal(total);
            setIsLoading(false);
        });
    }, [paginationPage, paginationItemsPerPage]);

    const openDeviationDialog = useCallback((deviationId: number) => {
        selectDeviationId(deviationId);
        setIsDeviationDialogOpen(true);
    }, [selectDeviationId, setIsDeviationDialogOpen]);

    const closeDeviationDialog = useCallback((deviationId?: number) => {
        if (deviationId) {
            selectDeviationId(deviationId);
        }
        setIsDeviationDialogOpen(false);
    }, [selectDeviationId, setIsDeviationDialogOpen]);

    useEffect(() => {
        if (!isDeviationDialogOpen) getData();
    }, [getData, isDeviationDialogOpen]);

    useEffect(() => {
        setPaginationPage(0);
    }, [paginationItemsPerPage]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>{t('pages.deviations.title')}</Title>
                {<GreenButton variant='outlined' onClick={() => openDeviationDialog(0)}>
                    {t('pages.deviations.new_deviation')}
                </GreenButton>}
            </FlexContainer>
            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('pages.deviations.table.category')}</TableCell>
                            <TableCell>{t('pages.deviations.table.description')}</TableCell>
                            <TableCell>{t('pages.generic.table.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {deviations.map(deviation => {
                            return (
                                <TableRow key={deviation.id}>
                                    <TableCell>{t('models.deviation.enum.category.' + deviation.category)}</TableCell>
                                    <TableCell>{deviation.description}</TableCell>
                                    <ActionTableCell>
                                        <StyledButton variant='outlined' color='primary' onClick={() => openDeviationDialog(deviation.id)}>
                                            {t('pages.generic.table.details')}
                                        </StyledButton>
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <CustomTablePagination
                count={paginationTotal}
                page={paginationPage}
                onPageChange={(_e, page) => setPaginationPage(page)}
                rowsPerPage={paginationItemsPerPage}
                onRowsPerPageChange={(e) => setPaginationItemsPerPage(parseInt(e.target.value, 10))}
            />

            <DeviationDetailsDialog open={isDeviationDialogOpen} onClose={closeDeviationDialog} deviationId={selectedDeviationId} />
        </>
    );
};

export default DeviationsPage;
